import { makeAutoObservable, runInAction } from 'mobx';
import createStoreContext from '../createStoreContext';
import {
  createWhatsHappeningDigestJob,
  getWhatsHappeningDigestActiveJob,
  getWhatsHappeningDigestJobStatus,
  searchWhatsHappeningDigest,
  listUnifiedDigests,
  getUnifiedDigestEmailPreferences,
  updateUnifiedDigestEmailPreferences,
} from 'src/services/unifiedDigest';
import { UNHANDLED_ERROR } from 'src/constants/errors';
import {
  UnifiedDigestJobStatus,
  UnifiedDigestActiveJobResponse,
  UnifiedDigestChatScope,
  UnifiedDigestEmailPreferenceUpdatePayload,
  UnifiedDigestResponseDto,
  UnifiedDigestEmailPreferenceResponse,
  UnifiedDigestState,
  UnifiedDigestTimeframe,
  UnifiedDigestRecapMode,
} from 'src/types/unifiedDigest';
import {
  UNIFIED_DIGEST_DEFAULT_TIMEFRAME,
  UNIFIED_DIGEST_JOB_STATUS,
  UNIFIED_DIGEST_JOB_STATUS_POLL_INTERVAL_MS,
  UNIFIED_DIGEST_HISTORY_LIMIT,
  UNIFIED_DIGEST_SEARCH_PAGE_SIZE,
  UNIFIED_DIGEST_JOB_FAILURE_MESSAGE,
} from 'src/constants/unifiedDigest';

const INITIAL_STATE: UnifiedDigestState = {
  isLoading: false,
  isRefreshing: false,
  error: null,
  errorCode: null,
  digest: undefined,
  digestHistory: [],
  digestHistoryCursor: null,
  digestHistoryCursorId: null,
  digestHistoryHasMore: false,
  isDigestHistoryLoadingMore: false,
  lastUpdated: undefined,
  timeframe: UNIFIED_DIGEST_DEFAULT_TIMEFRAME,
  searchResults: undefined,
  searchResultsHasMore: false,
  searchResultsNextOffset: null,
  isSearchResultsLoadingMore: false,
  isSearching: false,
  activeProcessingId: null,
  jobStatus: null,
  isPolling: false,
  hasActiveJob: false,
  activeJobStatus: null,
  activeJobTimeframe: null,
  selectedChatScope: null,
  activeJobWindow: null,
  activeJobScheduledAt: null,
  activeJobStartedAt: null,
  emailPreference: undefined,
  isEmailPreferenceLoading: false,
  isEmailPreferenceSaving: false,
  emailPreferenceError: null,
};

const getLastUpdatedTimestamp = (
  digest: UnifiedDigestResponseDto | undefined,
): string | undefined => {
  if (!digest) {
    return undefined;
  }

  return digest.generation?.generatedAt;
};

const createFailureState = (message?: string | null) => {
  return message ?? UNHANDLED_ERROR;
};

const JOB_STATUS_RETRYABLE_SET = new Set<UnifiedDigestJobStatus>([
  UNIFIED_DIGEST_JOB_STATUS.PENDING,
  UNIFIED_DIGEST_JOB_STATUS.RUNNING,
]);

type InactiveJobResolution = 'active' | 'failed' | 'complete';

// Provider scope keys can be absent on route input. Compare the actual scope,
// independent of presentation names and the ordering of selected spaces.
const chatScopeIdentity = (scope?: UnifiedDigestChatScope | null) =>
  JSON.stringify({
    mode: scope?.mode ?? null,
    spaceIds: Array.from(new Set(scope?.spaceIds ?? [])).sort(),
  });

export const [UnifiedDigestStoreProvider, useUnifiedDigestStore] =
  createStoreContext(
    class UnifiedDigestStore {
      state: UnifiedDigestState = { ...INITIAL_STATE };
      private fetchRequestCounter = 0;
      private activeFetchRequestId: number | null = null;
      private searchRequestCounter = 0;
      private viewRevision = 0;
      private activeJobPollingTimeoutId: ReturnType<typeof setTimeout> | null =
        null;

      constructor() {
        makeAutoObservable(this);
      }

      private setDigest(digest: UnifiedDigestResponseDto | undefined) {
        this.state.digest = digest;
        this.state.lastUpdated = getLastUpdatedTimestamp(digest);

        if (typeof digest !== 'undefined') {
          this.state.selectedChatScope = digest.chatScope ?? null;
        }
      }

      private setDigestHistory(digests: UnifiedDigestResponseDto[]) {
        this.state.digestHistory = digests;
      }

      private updateDigestHistoryPagination(
        digests: UnifiedDigestResponseDto[],
        hasMoreOverride?: boolean,
      ) {
        const lastDigest = digests[digests.length - 1];

        this.state.digestHistoryCursor = lastDigest?.windowEnd ?? null;
        this.state.digestHistoryCursorId = lastDigest?.id ?? null;
        this.state.digestHistoryHasMore =
          typeof hasMoreOverride === 'boolean'
            ? hasMoreOverride
            : digests.length >= UNIFIED_DIGEST_HISTORY_LIMIT;
      }

      private setError(
        message: string | null,
        errorCode?: string | number | null,
      ) {
        this.state.error = message;
        this.state.errorCode = errorCode ?? null;
      }

      private setEmailPreference(
        preference: UnifiedDigestEmailPreferenceResponse | undefined,
      ) {
        this.state.emailPreference = preference;
      }

      private setEmailPreferenceError(message: string | null) {
        this.state.emailPreferenceError = message;
      }

      private setSelectedChatScope(chatScope?: UnifiedDigestChatScope | null) {
        this.state.selectedChatScope = chatScope ?? null;
      }

      private resetSearch() {
        // Invalidates in-flight search responses when the visible window changes.
        this.searchRequestCounter += 1;
        this.state.searchResults = undefined;
        this.state.isSearching = false;
        this.state.isSearchResultsLoadingMore = false;
        this.state.searchResultsHasMore = false;
        this.state.searchResultsNextOffset = null;
      }

      private resetChangedView(
        timeframe: UnifiedDigestTimeframe,
        chatScope: UnifiedDigestChatScope | null,
        recapMode: UnifiedDigestRecapMode | undefined = this.state.recapMode,
      ) {
        // Never show a daily digest under a weekly/monthly tab while its
        // request is pending. Same-window refreshes retain usable content.
        const displayedTimeframe =
          this.state.digest?.timeframe ?? this.state.timeframe;
        const displayedScope = this.state.digest
          ? this.state.digest.chatScope
          : this.state.selectedChatScope;

        if (
          displayedTimeframe !== timeframe ||
          (this.state.recapMode ?? 'source_activity') !==
            (recapMode ?? 'source_activity') ||
          chatScopeIdentity(displayedScope) !== chatScopeIdentity(chatScope)
        ) {
          this.viewRevision += 1;
          this.setDigest(undefined);
          this.setDigestHistory([]);
          this.updateDigestHistoryPagination([], false);
          this.state.isDigestHistoryLoadingMore = false;
          this.resetSearch();
          this.resetActiveJobState();
        }
      }

      private currentViewGuard = () => {
        const revision = this.viewRevision;

        return () => revision === this.viewRevision;
      };

      private clearActiveJobPollingTimeout = () => {
        if (this.activeJobPollingTimeoutId) {
          clearTimeout(this.activeJobPollingTimeoutId);
          this.activeJobPollingTimeoutId = null;
        }
      };

      private resetActiveJobState = () => {
        this.clearActiveJobPollingTimeout();
        this.state.isPolling = false;
        this.state.activeProcessingId = null;
        this.state.jobStatus = null;
        this.state.hasActiveJob = false;
        this.state.activeJobStatus = null;
        this.state.activeJobTimeframe = null;
        this.state.selectedChatScope =
          this.state.digest?.chatScope ?? this.state.selectedChatScope ?? null;
        this.state.activeJobWindow = null;
        this.state.activeJobScheduledAt = null;
        this.state.activeJobStartedAt = null;
        this.state.isRefreshing = false;
      };

      private scheduleActiveJobPolling = (
        timeframe: UnifiedDigestTimeframe,
      ) => {
        this.clearActiveJobPollingTimeout();
        const isCurrentView = this.currentViewGuard();
        const chatScope = this.state.selectedChatScope;

        this.activeJobPollingTimeoutId = setTimeout(() => {
          this.activeJobPollingTimeoutId = null;
          if (isCurrentView()) void this.pollActiveJob(timeframe, chatScope);
        }, UNIFIED_DIGEST_JOB_STATUS_POLL_INTERVAL_MS);
      };

      private beginActiveJobPolling = (
        timeframe: UnifiedDigestTimeframe = this.state.timeframe,
        chatScope: UnifiedDigestChatScope | null = this.state.selectedChatScope,
      ) => {
        this.clearActiveJobPollingTimeout();
        this.setSelectedChatScope(chatScope);
        this.scheduleActiveJobPolling(timeframe);
      };

      private pollActiveJob = async (
        timeframe: UnifiedDigestTimeframe = this.state.timeframe,
        chatScope: UnifiedDigestChatScope | null = this.state.selectedChatScope,
      ) => {
        const isCurrentView = this.currentViewGuard();
        const wasActive = this.state.hasActiveJob;
        const previousProcessingId = this.state.activeProcessingId ?? null;
        const previousJobStatus = this.state.activeJobStatus ?? null;
        const isActive = await this.refreshActiveJob(
          timeframe,
          chatScope,
          isCurrentView,
        );

        if (!isCurrentView()) return;

        if (isActive) {
          this.scheduleActiveJobPolling(timeframe);

          return;
        }

        if (wasActive) {
          const inactiveResolution = await this.resolveInactiveJobStatus({
            processingId: previousProcessingId,
            previousStatus: previousJobStatus,
            timeframe,
            chatScope,
            isCurrentView,
          });

          if (!isCurrentView()) return;

          if (inactiveResolution === 'active') {
            this.scheduleActiveJobPolling(timeframe);

            return;
          }

          if (inactiveResolution === 'failed') {
            return;
          }

          void this.fetchDigest(timeframe, undefined, chatScope);
        }
      };

      private resolveInactiveJobStatus = async ({
        processingId,
        previousStatus,
        timeframe,
        chatScope,
        isCurrentView,
      }: {
        processingId: string | null;
        previousStatus: UnifiedDigestJobStatus | null;
        timeframe: UnifiedDigestTimeframe;
        chatScope: UnifiedDigestChatScope | null;
        isCurrentView: () => boolean;
      }): Promise<InactiveJobResolution> => {
        if (previousStatus === UNIFIED_DIGEST_JOB_STATUS.FAILED) {
          runInAction(() => {
            this.setError(UNIFIED_DIGEST_JOB_FAILURE_MESSAGE);
          });

          return 'failed';
        }

        if (!processingId) {
          return 'complete';
        }

        try {
          const response = await getWhatsHappeningDigestJobStatus(processingId);

          if (!isCurrentView()) return 'complete';

          if (!response.success || !response.content) {
            return 'complete';
          }

          const jobStatusResponse = response.content;

          if (jobStatusResponse.status === UNIFIED_DIGEST_JOB_STATUS.FAILED) {
            runInAction(() => {
              this.setError(
                jobStatusResponse.errorMessage ??
                  UNIFIED_DIGEST_JOB_FAILURE_MESSAGE,
                jobStatusResponse.errorCode ?? null,
              );
            });

            return 'failed';
          }

          if (JOB_STATUS_RETRYABLE_SET.has(jobStatusResponse.status)) {
            runInAction(() => {
              this.state.activeProcessingId = processingId;
              this.state.jobStatus = jobStatusResponse.status;
              this.state.hasActiveJob = true;
              this.state.activeJobStatus = jobStatusResponse.status;
              this.state.activeJobTimeframe = timeframe;
              this.setSelectedChatScope(
                jobStatusResponse.digest?.chatScope ?? chatScope,
              );
            });

            return 'active';
          }
        } catch {}

        return 'complete';
      };

      fetchEmailPreference = async () => {
        runInAction(() => {
          this.state.isEmailPreferenceLoading = true;
          this.setEmailPreferenceError(null);
        });

        const response = await getUnifiedDigestEmailPreferences();

        runInAction(() => {
          this.state.isEmailPreferenceLoading = false;

          if (!response.success || !response.content) {
            this.setEmailPreference(undefined);
            this.setEmailPreferenceError(response.message ?? UNHANDLED_ERROR);

            return;
          }

          this.setEmailPreference(response.content);
        });
      };

      updateEmailPreference = async (
        payload: UnifiedDigestEmailPreferenceUpdatePayload,
      ) => {
        runInAction(() => {
          this.state.isEmailPreferenceSaving = true;
          this.setEmailPreferenceError(null);
        });

        const response = await updateUnifiedDigestEmailPreferences(payload);

        runInAction(() => {
          this.state.isEmailPreferenceSaving = false;

          if (!response.success || !response.content) {
            this.setEmailPreferenceError(response.message ?? UNHANDLED_ERROR);

            return;
          }

          this.setEmailPreference(response.content);
        });

        return response;
      };

      fetchDigest = async (
        timeframe: UnifiedDigestTimeframe = this.state.timeframe,
        referenceDate?: string,
        chatScope: UnifiedDigestChatScope | null = this.state.selectedChatScope,
        recapMode: UnifiedDigestRecapMode | undefined = this.state.recapMode,
      ) => {
        const requestId = ++this.fetchRequestCounter;
        const shouldPreserveJobState =
          this.state.hasActiveJob &&
          this.state.timeframe === timeframe &&
          (this.state.recapMode ?? 'source_activity') ===
            (recapMode ?? 'source_activity') &&
          chatScopeIdentity(this.state.selectedChatScope) ===
            chatScopeIdentity(chatScope);

        this.activeFetchRequestId = requestId;

        runInAction(() => {
          this.resetChangedView(timeframe, chatScope, recapMode);
          this.state.recapMode = recapMode;
          this.state.isLoading = true;
          this.setError(null);
          this.state.timeframe = timeframe;
          this.setSelectedChatScope(chatScope);
          this.state.isDigestHistoryLoadingMore = false;
          this.state.digestHistoryCursor = null;
          this.state.digestHistoryCursorId = null;
          this.state.digestHistoryHasMore = false;

          if (!shouldPreserveJobState) {
            this.resetActiveJobState();
          }
        });
        const isCurrentView = this.currentViewGuard();

        try {
          const response = await listUnifiedDigests({
            ...(recapMode ? { recapMode } : {}),
            timeframe,
            chatScope,
            before: referenceDate,
            limit: UNIFIED_DIGEST_HISTORY_LIMIT,
          });

          runInAction(() => {
            if (this.activeFetchRequestId !== requestId || !isCurrentView()) {
              return;
            }

            this.activeFetchRequestId = null;
            this.state.isLoading = false;

            if (!response.success || !response.content) {
              this.setDigest(undefined);
              this.setDigestHistory([]);
              this.updateDigestHistoryPagination([], false);
              this.setError(
                createFailureState(response.message),
                response.errorCode ?? null,
              );

              return;
            }

            if (!shouldPreserveJobState) {
              this.resetActiveJobState();
            }

            this.setDigestHistory(response.content);
            this.setDigest(response.content[0]);
            this.updateDigestHistoryPagination(response.content);
          });
        } catch (error: any) {
          runInAction(() => {
            if (this.activeFetchRequestId !== requestId || !isCurrentView()) {
              return;
            }

            this.activeFetchRequestId = null;
            this.state.isLoading = false;

            if (!shouldPreserveJobState) {
              this.resetActiveJobState();
            }

            this.setDigest(undefined);
            this.setDigestHistory([]);
            this.updateDigestHistoryPagination([], false);
            this.setError(error?.message ?? UNHANDLED_ERROR);
          });
        }
      };

      initializeDigest = async (params?: {
        timeframe?: UnifiedDigestTimeframe;
        chatScope?: UnifiedDigestChatScope | null;
        recapMode?: UnifiedDigestRecapMode;
      }) => {
        const timeframe = params?.timeframe ?? this.state.timeframe;
        const chatScope =
          params?.chatScope === undefined
            ? this.state.selectedChatScope
            : params.chatScope;
        const recapMode =
          params && 'recapMode' in params
            ? params.recapMode
            : this.state.recapMode;

        runInAction(() => {
          this.resetChangedView(timeframe, chatScope, recapMode);
          this.state.recapMode = recapMode;
          this.state.timeframe = timeframe;
          this.setSelectedChatScope(chatScope);
        });
        const requestedViewRevision = this.viewRevision;
        const isCurrentView = () => this.viewRevision === requestedViewRevision;

        const hasActiveJob = await this.refreshActiveJob(
          timeframe,
          chatScope,
          isCurrentView,
        );

        if (!isCurrentView()) return;

        if (hasActiveJob) {
          this.beginActiveJobPolling(timeframe, chatScope);
        }

        await this.fetchDigest(timeframe, undefined, chatScope);
      };

      refreshActiveJob = async (
        timeframe: UnifiedDigestTimeframe = this.state.timeframe,
        chatScope: UnifiedDigestChatScope | null = this.state.selectedChatScope,
        isCurrentView: () => boolean = this.currentViewGuard(),
      ): Promise<boolean> => {
        let activeJob: UnifiedDigestActiveJobResponse | null = null;
        let encounteredError = false;

        try {
          const response = await getWhatsHappeningDigestActiveJob(
            timeframe,
            chatScope,
            ...(this.state.recapMode ? [this.state.recapMode] : []),
          );

          if (response.success && response.content) {
            activeJob = response.content;
          }
        } catch (error: any) {
          if (!isCurrentView()) return false;
          encounteredError = true;
          this.setError(error?.message ?? UNHANDLED_ERROR);
        }

        if (!isCurrentView()) return false;

        if (activeJob && JOB_STATUS_RETRYABLE_SET.has(activeJob.status)) {
          const jobSnapshot = activeJob;

          runInAction(() => {
            this.state.activeProcessingId = jobSnapshot.processingId;
            this.state.jobStatus = jobSnapshot.status;
            this.state.hasActiveJob = true;
            this.state.activeJobStatus = jobSnapshot.status;
            this.state.activeJobTimeframe = jobSnapshot.timeframe;
            this.setSelectedChatScope(jobSnapshot.chatScope ?? chatScope);
            this.state.activeJobWindow = {
              start: jobSnapshot.windowStart,
              end: jobSnapshot.windowEnd,
            };
            this.state.activeJobScheduledAt = jobSnapshot.scheduledAt ?? null;
            this.state.activeJobStartedAt = jobSnapshot.startedAt ?? null;
          });

          return true;
        }

        if (encounteredError) {
          return this.state.hasActiveJob;
        }

        runInAction(() => {
          this.resetActiveJobState();
        });

        return false;
      };

      refreshDigest = async (): Promise<'started' | 'active' | 'error'> => {
        const isCurrentView = this.currentViewGuard();
        const timeframe = this.state.timeframe;
        const chatScope = this.state.selectedChatScope;

        if (this.state.isRefreshing) {
          return 'active';
        }

        if (this.state.hasActiveJob) {
          this.beginActiveJobPolling(
            this.state.activeJobTimeframe ?? this.state.timeframe,
            this.state.selectedChatScope,
          );

          return 'active';
        }

        const hasActiveJob = await this.refreshActiveJob(
          timeframe,
          chatScope,
          isCurrentView,
        );

        if (!isCurrentView()) return 'error';

        if (hasActiveJob) {
          this.beginActiveJobPolling(
            this.state.timeframe,
            this.state.selectedChatScope,
          );

          return 'active';
        }

        runInAction(() => {
          this.state.isRefreshing = true;
          this.setError(null);
        });

        try {
          const response = await createWhatsHappeningDigestJob({
            ...(this.state.recapMode
              ? { recapMode: this.state.recapMode }
              : {}),
            timeframe,
            chatScope,
            forceRegenerate: true,
          });

          if (!isCurrentView()) return 'error';

          if (!response.success || !response.content) {
            runInAction(() => {
              this.state.isRefreshing = false;
              this.setError(
                createFailureState(response.message),
                response.errorCode ?? null,
              );
            });

            return 'error';
          }

          const { processingId } = response.content;

          runInAction(() => {
            this.state.activeProcessingId = processingId;
            this.state.jobStatus = UNIFIED_DIGEST_JOB_STATUS.PENDING;
            this.state.hasActiveJob = true;
            this.state.activeJobStatus = UNIFIED_DIGEST_JOB_STATUS.PENDING;
            this.state.activeJobTimeframe = this.state.timeframe;
            this.setSelectedChatScope(this.state.selectedChatScope);
            this.state.activeJobWindow = null;
            this.state.activeJobScheduledAt = null;
            this.state.activeJobStartedAt = null;
            this.state.isRefreshing = false;
          });

          this.beginActiveJobPolling(
            this.state.timeframe,
            this.state.selectedChatScope,
          );

          return 'started';
        } catch (error: any) {
          if (!isCurrentView()) return 'error';
          runInAction(() => {
            this.state.isRefreshing = false;
            this.setError(error?.message ?? UNHANDLED_ERROR);
          });

          return 'error';
        }
      };

      searchDigest = async (query: string) => {
        const trimmedQuery = query.trim();

        if (trimmedQuery === '') {
          runInAction(() => {
            this.resetSearch();
          });

          return;
        }

        const requestId = ++this.searchRequestCounter;

        runInAction(() => {
          this.state.isSearching = true;
          this.state.searchResults = undefined;
          this.state.searchResultsHasMore = false;
          this.state.searchResultsNextOffset = null;
          this.state.isSearchResultsLoadingMore = false;
        });

        try {
          const response = await searchWhatsHappeningDigest(
            trimmedQuery,
            this.state.timeframe,
            UNIFIED_DIGEST_SEARCH_PAGE_SIZE,
            0,
            undefined,
            this.state.selectedChatScope,
            ...(this.state.recapMode ? [this.state.recapMode] : []),
          );

          runInAction(() => {
            if (requestId !== this.searchRequestCounter) return;
            this.state.isSearching = false;

            if (!response.success || !response.content) {
              this.state.searchResults = undefined;
              this.state.searchResultsHasMore = false;
              this.state.searchResultsNextOffset = null;
              this.setError(
                createFailureState(response.message),
                response.errorCode ?? null,
              );

              return;
            }

            this.state.searchResults = response.content;
            this.state.searchResultsNextOffset =
              response.content.nextOffset ?? null;
            this.state.searchResultsHasMore =
              typeof response.content.nextOffset === 'number';
          });
        } catch (error: any) {
          runInAction(() => {
            if (requestId !== this.searchRequestCounter) return;
            this.state.isSearching = false;
            this.state.searchResults = undefined;
            this.state.searchResultsHasMore = false;
            this.state.searchResultsNextOffset = null;
            this.setError(error?.message ?? UNHANDLED_ERROR);
          });
        }
      };

      clearSearchResults = () => {
        runInAction(() => {
          this.resetSearch();
          this.state.searchResultsHasMore = false;
          this.state.searchResultsNextOffset = null;
        });
      };

      loadMoreDigestHistory = async () => {
        if (this.state.isDigestHistoryLoadingMore) {
          return;
        }

        if (!this.state.digestHistoryHasMore) {
          return;
        }

        const before = this.state.digestHistoryCursor;
        const beforeId = this.state.digestHistoryCursorId ?? undefined;
        const requestedTimeframe = this.state.timeframe;
        const requestedCursorId = beforeId ?? null;
        const requestedFetchId = this.fetchRequestCounter;
        const isCurrentView = this.currentViewGuard();

        if (!before) {
          return;
        }

        runInAction(() => {
          this.state.isDigestHistoryLoadingMore = true;
          this.setError(null);
        });

        try {
          const response = await listUnifiedDigests({
            timeframe: this.state.timeframe,
            ...(this.state.recapMode
              ? { recapMode: this.state.recapMode }
              : {}),
            chatScope: this.state.selectedChatScope,
            before,
            beforeId,
            limit: UNIFIED_DIGEST_HISTORY_LIMIT,
          });

          runInAction(() => {
            const isStaleRequest =
              !isCurrentView() ||
              this.fetchRequestCounter !== requestedFetchId ||
              this.state.timeframe !== requestedTimeframe ||
              this.state.digestHistoryCursor !== before ||
              (this.state.digestHistoryCursorId ?? null) !== requestedCursorId;

            if (isStaleRequest) {
              return;
            }

            this.state.isDigestHistoryLoadingMore = false;

            if (!response.success || !response.content) {
              this.setError(
                createFailureState(response.message),
                response.errorCode ?? null,
              );

              return;
            }

            if (response.content.length === 0) {
              this.updateDigestHistoryPagination(
                this.state.digestHistory,
                false,
              );

              return;
            }

            const existingIds = new Set(
              this.state.digestHistory.map((digest) => digest.id),
            );
            const nextPage = response.content.filter(
              (digest) => !existingIds.has(digest.id),
            );
            const merged = [...this.state.digestHistory, ...nextPage];

            this.setDigestHistory(merged);
            this.updateDigestHistoryPagination(
              merged,
              response.content.length >= UNIFIED_DIGEST_HISTORY_LIMIT,
            );
          });
        } catch (error: any) {
          runInAction(() => {
            const isStaleRequest =
              !isCurrentView() ||
              this.fetchRequestCounter !== requestedFetchId ||
              this.state.timeframe !== requestedTimeframe ||
              this.state.digestHistoryCursor !== before ||
              (this.state.digestHistoryCursorId ?? null) !== requestedCursorId;

            if (isStaleRequest) {
              return;
            }

            this.state.isDigestHistoryLoadingMore = false;

            this.setError(error?.message ?? UNHANDLED_ERROR);
          });
        }
      };

      loadMoreSearchResults = async () => {
        if (this.state.isSearchResultsLoadingMore) {
          return;
        }

        if (!this.state.searchResultsHasMore) {
          return;
        }

        const searchQuery = this.state.searchResults?.query;
        const nextOffset = this.state.searchResultsNextOffset ?? null;
        const requestedTimeframe = this.state.timeframe;
        const requestId = this.searchRequestCounter;

        if (!searchQuery || typeof nextOffset !== 'number') {
          return;
        }

        runInAction(() => {
          this.state.isSearchResultsLoadingMore = true;
          this.setError(null);
        });

        try {
          const response = await searchWhatsHappeningDigest(
            searchQuery,
            this.state.timeframe,
            UNIFIED_DIGEST_SEARCH_PAGE_SIZE,
            nextOffset,
            undefined,
            this.state.selectedChatScope,
            ...(this.state.recapMode ? [this.state.recapMode] : []),
          );

          runInAction(() => {
            if (requestId !== this.searchRequestCounter) return;
            this.state.isSearchResultsLoadingMore = false;
            const isStaleRequest =
              this.state.timeframe !== requestedTimeframe ||
              this.state.searchResults?.query !== searchQuery ||
              this.state.searchResultsNextOffset !== nextOffset;

            if (isStaleRequest) {
              return;
            }

            if (!response.success || !response.content) {
              this.setError(
                createFailureState(response.message),
                response.errorCode ?? null,
              );

              return;
            }

            const existingResults = this.state.searchResults?.results ?? [];
            const mergedResults = [
              ...existingResults,
              ...response.content.results,
            ];

            this.state.searchResults = {
              ...response.content,
              results: mergedResults,
              generatedSummary:
                this.state.searchResults?.generatedSummary ??
                response.content.generatedSummary,
            };
            this.state.searchResultsNextOffset =
              response.content.nextOffset ?? null;
            this.state.searchResultsHasMore =
              typeof response.content.nextOffset === 'number';
          });
        } catch (error: any) {
          runInAction(() => {
            if (requestId !== this.searchRequestCounter) return;
            this.state.isSearchResultsLoadingMore = false;

            const isStaleRequest =
              this.state.timeframe !== requestedTimeframe ||
              this.state.searchResults?.query !== searchQuery ||
              this.state.searchResultsNextOffset !== nextOffset;

            if (isStaleRequest) {
              return;
            }

            this.setError(error?.message ?? UNHANDLED_ERROR);
          });
        }
      };

      selectDigest = (digest: UnifiedDigestResponseDto) => {
        runInAction(() => {
          this.setDigest(digest);
        });
      };

      dispose = () => {
        this.viewRevision += 1;
        this.activeFetchRequestId = null;
        this.searchRequestCounter += 1;
        this.clearActiveJobPollingTimeout();

        runInAction(() => {
          this.state.isPolling = false;
          this.state.activeProcessingId = null;
          this.state.isRefreshing = false;
          this.state.jobStatus = null;
          this.state.hasActiveJob = false;
          this.state.activeJobStatus = null;
          this.state.activeJobTimeframe = null;
          this.state.selectedChatScope = null;
          this.state.activeJobWindow = null;
          this.state.activeJobScheduledAt = null;
          this.state.activeJobStartedAt = null;
        });
      };
    },
  );

export type UnifiedDigestStore = ReturnType<typeof useUnifiedDigestStore>;
